import { formatHour12, hasWeeklyAvailability, weeklyAvailabilityRows } from './availabilitySummary';
import { getDefaultTimeZoneOnBrowser, viewerDisplayTimeZone } from './dates';

export const TALKER_AVAILABILITY_KEY = 'talkerAvailability';

export const TALKER_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const WEEKDAY_FROM_JS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const HHMM_RE = /^(\d{1,2}):([0-5]\d)$/;

/**
 * Parse HH:mm (or 24:00) to minutes from midnight.
 *
 * @param {string} hhmm
 * @returns {number}
 */
export const minutesFromHhmm = hhmm => {
  if (hhmm === '24:00') {
    return 24 * 60;
  }
  const match = typeof hhmm === 'string' ? hhmm.trim().match(HHMM_RE) : null;
  if (!match) {
    return NaN;
  }
  const hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  if (hour > 24 || (hour === 24 && minute !== 0)) {
    return NaN;
  }
  return hour * 60 + minute;
};

/**
 * Format minutes from midnight as HH:mm (24:00 allowed).
 *
 * @param {number} minutes
 * @returns {string}
 */
export const hhmmFromMinutes = minutes => {
  if (!Number.isFinite(minutes)) {
    return '';
  }
  if (minutes >= 24 * 60) {
    return '24:00';
  }
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${hour > 9 ? hour : `0${hour}`}:${minute > 9 ? minute : `0${minute}`}`;
};

const normalizeEntry = entry => {
  const dayOfWeek = TALKER_WEEKDAYS.includes(entry?.dayOfWeek) ? entry.dayOfWeek : null;
  const startTime = entry?.startTime || entry?.start || null;
  const rawEnd = entry?.endTime || entry?.end || null;
  const endTime = rawEnd === '00:00' ? '24:00' : rawEnd;
  const startMinutes = minutesFromHhmm(startTime);
  const endMinutes = minutesFromHhmm(endTime);
  if (!dayOfWeek || !Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) {
    return null;
  }
  if (endMinutes <= startMinutes) {
    return null;
  }
  return { dayOfWeek, startTime, endTime };
};

/**
 * Browser timezone, or UTC on the server.
 *
 * @returns {string}
 */
export const defaultTalkerTimeZone = () => {
  if (typeof window === 'undefined') {
    return 'Etc/UTC';
  }
  return getDefaultTimeZoneOnBrowser() || viewerDisplayTimeZone() || 'Etc/UTC';
};

/**
 * Normalize user publicData.talkerAvailability to a weekly plan.
 *
 * @param {Object} [raw]
 * @returns {{ timezone: string, entries: Array<{ dayOfWeek: string, startTime: string, endTime: string }> }}
 */
export const normalizeTalkerAvailability = (raw = {}) => {
  const timezone =
    typeof raw?.timezone === 'string' && raw.timezone.trim() ? raw.timezone.trim() : defaultTalkerTimeZone();
  const entries = (Array.isArray(raw?.entries) ? raw.entries : []).map(normalizeEntry).filter(Boolean);
  return { timezone, entries };
};

/**
 * Talker weekly plan from a user entity.
 *
 * @param {Object} [user]
 * @returns {{ timezone: string, entries: Array }}
 */
export const talkerAvailabilityFromUser = user =>
  normalizeTalkerAvailability(user?.attributes?.profile?.publicData?.[TALKER_AVAILABILITY_KEY]);

/**
 * Profile update payload that only touches talkerAvailability.
 *
 * @param {Object} [plan]
 * @returns {{ publicData: Object }}
 */
export const buildTalkerAvailabilityUpdate = (plan = {}) => ({
  publicData: {
    [TALKER_AVAILABILITY_KEY]: normalizeTalkerAvailability(plan),
  },
});

/**
 * Weekday key for a date in the plan timezone.
 *
 * @param {Date|string|number} date
 * @param {string} [timeZone]
 * @returns {string|null}
 */
const isUtcTimeZone = timeZone =>
  !timeZone || timeZone === 'Etc/UTC' || timeZone === 'UTC' || timeZone === 'GMT';

const toMs = date => {
  if (date instanceof Date) {
    return date.getTime();
  }
  if (typeof date === 'number') {
    return date;
  }
  return Date.parse(date);
};

export const talkerDayOfWeek = (date, timeZone) => {
  const ms = toMs(date);
  if (!Number.isFinite(ms)) {
    return null;
  }
  if (isUtcTimeZone(timeZone)) {
    return WEEKDAY_FROM_JS[new Date(ms).getUTCDay()] || null;
  }
  try {
    const weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone,
    }).format(new Date(ms));
    const key = weekday.slice(0, 3).toLowerCase();
    return WEEKDAY_FROM_JS.includes(key) ? key : null;
  } catch (e) {
    return WEEKDAY_FROM_JS[new Date(ms).getUTCDay()] || null;
  }
};

/**
 * Minutes from midnight in the plan timezone.
 *
 * @param {Date|string|number} date
 * @param {string} [timeZone]
 * @returns {number}
 */
export const talkerMinutesOfDay = (date, timeZone) => {
  const ms = toMs(date);
  if (!Number.isFinite(ms)) {
    return NaN;
  }
  if (isUtcTimeZone(timeZone)) {
    const utc = new Date(ms);
    return utc.getUTCHours() * 60 + utc.getUTCMinutes();
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
      timeZone,
    }).formatToParts(new Date(ms));
    const hour = Number.parseInt(parts.find(part => part.type === 'hour')?.value, 10);
    const minute = Number.parseInt(parts.find(part => part.type === 'minute')?.value, 10);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return NaN;
    }
    return hour * 60 + minute;
  } catch (e) {
    const utc = new Date(ms);
    return utc.getUTCHours() * 60 + utc.getUTCMinutes();
  }
};

/**
 * True when [start, end) sits inside a talker weekly entry.
 * An empty plan does not block booking (listener hours stay in charge).
 *
 * @param {Date|string|number} start
 * @param {Date|string|number} end
 * @param {Object} [plan]
 * @returns {boolean}
 */
export const isRangeWithinTalkerPlan = (start, end, plan) => {
  const normalized = normalizeTalkerAvailability(plan);
  if (!hasWeeklyAvailability(normalized)) {
    return true;
  }
  const startMs = toMs(start);
  const endMs = toMs(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return false;
  }
  const { timezone, entries } = normalized;
  const day = talkerDayOfWeek(startMs, timezone);
  if (!day || talkerDayOfWeek(endMs - 1, timezone) !== day) {
    return false;
  }
  const startMinutes = talkerMinutesOfDay(startMs, timezone);
  const endMinutes = talkerMinutesOfDay(endMs, timezone) || 24 * 60;
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) {
    return false;
  }
  return entries.some(entry => {
    if (entry.dayOfWeek !== day) {
      return false;
    }
    const entryStart = minutesFromHhmm(entry.startTime);
    const entryEnd = minutesFromHhmm(entry.endTime);
    return startMinutes >= entryStart && endMinutes <= entryEnd;
  });
};

const slotBounds = slot => {
  const attrs = slot?.attributes || slot || {};
  return { start: attrs.start, end: attrs.end };
};

/**
 * Keep Sharetribe time slots that overlap the talker's open hours.
 *
 * @param {Array} [timeSlots]
 * @param {Object} [plan]
 * @returns {Array}
 */
export const filterTimeSlotsByTalkerAvailability = (timeSlots = [], plan) => {
  const normalized = normalizeTalkerAvailability(plan);
  if (!hasWeeklyAvailability(normalized)) {
    return timeSlots;
  }
  return (Array.isArray(timeSlots) ? timeSlots : []).filter(slot => {
    const { start, end } = slotBounds(slot);
    return isRangeWithinTalkerPlan(start, end, normalized);
  });
};

/**
 * Filter monthlyTimeSlots / timeSlotsForDate maps without dropping fetch state.
 *
 * @param {Object} [slotState]
 * @param {Object} [plan]
 * @returns {Object}
 */
export const filterTimeSlotStateByTalkerAvailability = (slotState = {}, plan) => {
  const normalized = normalizeTalkerAvailability(plan);
  if (!hasWeeklyAvailability(normalized)) {
    return slotState;
  }
  return Object.fromEntries(
    Object.entries(slotState || {}).map(([key, value]) => [
      key,
      value && Array.isArray(value.timeSlots)
        ? { ...value, timeSlots: filterTimeSlotsByTalkerAvailability(value.timeSlots, normalized) }
        : value,
    ])
  );
};

/**
 * Hour options for the schedule editor (12-hour labels, 24-hour values).
 *
 * @param {boolean} [isEnd]
 * @returns {Array<{ value: string, label: string }>}
 */
export const talkerHourOptions = (isEnd = false) => {
  const hours = [];
  const start = isEnd ? 1 : 0;
  const stop = isEnd ? 24 : 23;
  for (let hour = start; hour <= stop; hour += 1) {
    const value = hour === 24 ? '24:00' : hhmmFromMinutes(hour * 60);
    hours.push({ value, label: formatHour12(value) });
  }
  return hours;
};

/**
 * Upcoming 15-minute open slots from a talker plan (next N days).
 *
 * @param {Object} [plan]
 * @param {Object} [opts]
 * @param {Date} [opts.now]
 * @param {number} [opts.days]
 * @param {number} [opts.minutes]
 * @param {number} [opts.limit]
 * @returns {Array<{ start: Date, end: Date, day: string, label: string }>}
 */
export const upcomingTalkerSlots = (
  plan,
  { now = new Date(), days = 7, minutes = 15, limit = 24 } = {}
) => {
  const normalized = normalizeTalkerAvailability(plan);
  if (!hasWeeklyAvailability(normalized)) {
    return [];
  }
  const lengthMs = minutes * 60 * 1000;
  const startMs = now.getTime();
  const endMs = startMs + days * 24 * 60 * 60 * 1000;
  const first = Math.ceil(startMs / lengthMs) * lengthMs;
  const slots = [];
  for (let cursor = first; cursor < endMs && slots.length < limit; cursor += lengthMs) {
    const start = new Date(cursor);
    const end = new Date(cursor + lengthMs);
    if (!isRangeWithinTalkerPlan(start, end, normalized)) {
      continue;
    }
    slots.push({
      start,
      end,
      day: talkerDayOfWeek(start, normalized.timezone),
      label: `${formatHour12(hhmmFromMinutes(talkerMinutesOfDay(start, normalized.timezone)))}–${formatHour12(
        hhmmFromMinutes(talkerMinutesOfDay(end, normalized.timezone) || 24 * 60)
      )}`,
    });
  }
  return slots;
};

export { formatHour12, hasWeeklyAvailability, weeklyAvailabilityRows };
